import React, {useEffect} from 'react'
import { observer, useLocalObservable } from "mobx-react-lite"
import Divider from '@mui/material/Divider';
import OutlinedInput from '@mui/material/OutlinedInput';
import FormControl from '@mui/material/FormControl';
import Link from '@mui/material/Link';
import LoadingButton from '@mui/lab/LoadingButton';
import clsx from 'clsx';
import { useNavigate, useLocation } from "@reach/router"

import GoogleButton from './GoogleButton'
import FacebookButton from './FacebookButton';

import classes from './Signup.module.scss'
import {ReactComponent as Logo} from '../../logo3.svg'
import Password from '../account/Password'
import { createApi } from '../common/API'
import userStore from '../common/userStore';
import useTitle from '../../hooks/title';
import { getRedirectUrl } from '../../utils'


const userRegister = createApi('/user/register', {method: 'POST', expandData: true, notify: true, noThrow: false});

const InfoItem = ({children}) => {
  return <div className={classes.item}>
    <div className={classes.itemIcon}>✓</div>
    <div className={classes.itemBody}>
    {children}
    </div>
  </div>
}

const Signup = observer(() => {
  const inputClasses = {'root':classes.input, 'notchedOutline': classes.notchedOutline}
  const navigate = useNavigate()
  const location = useLocation();
  useTitle('Signup')

  const toLogin = () => {
    const search = location.search;
    navigate('/auth/login' + search, {replace: true})
  }

  const form = useLocalObservable(() => ({
    email: '',
    password: '',
    posting: false,
    immediately: false,
    errors: {email: null, password: null},
    handleEmail(e) {
      this.email = e.target.value
      this.immediately && this.validEmail()
    },
    handlePassword(e) {
      this.password = e.target.value
      this.immediately && this.validPassword()
    },
    validEmail() {
      if (this.email.length < 1) {
        this.errors.email = 'Email is required'
        return false
      }
      const pattern = /[\w!#$%&'*+/=?^_`{|}~-]+(?:\.[\w!#$%&'*+/=?^_`{|}~-]+)*@(?:[\w](?:[\w-]*[\w])?\.)+[\w](?:[\w-]*[\w])?/g
      if (!(pattern.test(this.email.toString()))) {
        this.errors.email = 'Email is invalid'
        return false
      }
      this.errors.email = null
      return true
    },
    validPassword() {
      if (this.password.length < 1) {
        this.errors.password = 'Password is required'
        return false
      }
      if (this.password.length < 8) {
        this.errors.password = 'At least 8 characters in length'
        return false
      }
      this.errors.password = null
      return true
    },
    handleKeyup(e) {
      if(e.keyCode === 13) {
        this.handleSubmit()
      }
    },
    async handleSubmit() {
      try {
        this.posting = true
        this.immediately = true
        const ck = [this.validEmail(), this.validPassword()]
        if (ck.some(c => !c)) {
          return;
        }
        await userRegister({
          email: this.email,
          password: this.password
        })
        userStore.reload()
        const rd = getRedirectUrl()
        if (rd) {
          navigate(rd, {replace: true})
          return
        }
        navigate('/', {replace: true})
      } catch (e) {
        console.log('reg fail', e)
        this.errors.email = 'Email already existed'
      } finally {
        this.posting = false
      }
    },
  }))

  useEffect(() => {
    if (userStore.needMerge) {
      navigate('/auth/whoops')
      return;
    }
    if (userStore.isLogin) {
      navigate('/')
    }
  }, [navigate, userStore.isLogin, userStore.needMerge])

  return <div className={classes.center}>
      <div className={classes.left}>
        <Logo className={classes.logo} onClick={() => navigate('/')} />
        <div className={classes.subtitle}>
        Sign up today and start exploring Session Loops for FREE.
        </div>
        <InfoItem>Access the next generation music production tool Loop Editor.</InfoItem>
        <InfoItem>Build your own loops from scratch or pick some from presets.</InfoItem>
        <InfoItem>Modify chord changes, FX chains and registers to fit your projects.</InfoItem>
        <InfoItem>Download high-quality, royalty-free samples instantly.</InfoItem>
      </div>
      <div className={classes.right}>
        <div className={classes.bg}></div>
        <Logo className={classes.rightLogo} onClick={() => navigate('/')} />
        <div className={classes.signupTips}>
          Make custom loops today
        </div>
        <FormControl className={clsx(classes.field, classes.fieldEmail)}>
          <OutlinedInput
            classes={inputClasses}
            name="email"
            size="small"
            placeholder="Email address"
            onChange={form.handleEmail}
            onKeyUp={form.handleKeyup} />
        </FormControl>
        <div className={classes.error}>
          {form.errors.email}
        </div>
        <FormControl className={classes.field}>
          <Password classes={inputClasses}
            name="password"
            size="small"
            type="password"
            placeholder="Password: 8+ characters"
            onChange={form.handlePassword}
            onKeyUp={form.handleKeyup} />
        </FormControl>
        <div className={classes.error}>
          {form.errors.password}
        </div>

        <LoadingButton
          className={classes.submit}
          variant="contained"
          disableElevation
          loading={form.posting}
          onClick={form.handleSubmit}
          >Create Account</LoadingButton>

        <Divider className={classes.divider} light={true}>or continue with</Divider>

        <div className={classes.actions}>
          <GoogleButton className={classes.google} />
          {/* <FacebookButton className={classes.facebook} /> */}
        </div>

        <div className={classes.info}>
        By signing up, you agree to our <Link underline="none" onClick={() => navigate('/policies/terms-of-service')}>Terms</Link> and <Link underline="none" onClick={() => navigate('/policies/privacy-policy')}>Privacy Policy</Link>
        <br />
        Already have an account? <Link underline="none" onClick={toLogin}>Log in</Link>
        </div>
      </div>
    </div>
})

export default Signup
